import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Stack,
  Button,
  Typography,
} from "@mui/material";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import KeyboardDoubleArrowUpIcon from "@mui/icons-material/KeyboardDoubleArrowUp";
import KeyboardDoubleArrowDownIcon from "@mui/icons-material/KeyboardDoubleArrowDown";

const LayerToolbar = ({ canvas, saveHistory, setSelectedObject }) => {
  const [layers, setLayers] = useState([]);

  // Update layer properties (visibility)
  const updateLayerProperties = useCallback((objects) => {
    objects.forEach((layer) => {
      layer.visible = true;
    });
    return objects;
  }, []);

  // Update layers state from canvas objects
  const updateLayers = useCallback(() => {
    if (!canvas) return;
    let objects = canvas.getObjects();
    objects = updateLayerProperties(objects);
    objects = objects.slice().reverse(); // Reverse for top-to-bottom display
    setLayers(
      objects.map((obj, index) => ({
        name: obj.name || obj.type || `Layer ${objects.length - index}`,
        object: obj,
        isSelected: canvas.getActiveObject() === obj,
      }))
    );
  }, [canvas, updateLayerProperties]);

  useEffect(() => {
    if (!canvas) return;

    updateLayers();
    canvas.on("object:added", updateLayers);
    canvas.on("object:removed", updateLayers);
    canvas.on("object:modified", updateLayers);
    canvas.on("selection:created", updateLayers);
    canvas.on("selection:updated", updateLayers);
    canvas.on("selection:cleared", updateLayers);

    return () => {
      canvas.off("object:added", updateLayers);
      canvas.off("object:removed", updateLayers);
      canvas.off("object:modified", updateLayers);
      canvas.off("selection:created", updateLayers);
      canvas.off("selection:updated", updateLayers);
      canvas.off("selection:cleared", updateLayers);
    };
  }, [canvas, updateLayers]);

  // Select a layer and update canvas
  const handleSelectLayer = (layer) => {
    if (!canvas || !layer.object) return;
    canvas.setActiveObject(layer.object);
    canvas.requestRenderAll();
    setSelectedObject(layer.object);
    updateLayers();
    console.log("LayerToolbar: Selected layer", {
      name: layer.name,
    });
  };

  // Move layer up one step
  const handleBringForward = (layer) => {
    if (!canvas || !layer.object) return;
    const object = canvas.getActiveObject();
    canvas.bringObjectForward(object, true);
    canvas.requestRenderAll();
    saveHistory(canvas);
    console.log("LayerToolbar: Brought forward", {
      name: layer.name,
    });
  };

  // Move layer down one step
  const handleSendBackward = (layer) => {
    if (!canvas || !layer.object) return;
    const object = canvas.getActiveObject();
    canvas.sendObjectBackwards(object, true);
    canvas.requestRenderAll();
    saveHistory(canvas);
    console.log("LayerToolbar: Sent backward", {
      name: layer.name,
    });
  };

  // Move layer to front
  const handleSendToFront = () => {
    const object = canvas.getActiveObject();
    if (!canvas || !object) return;
    canvas.bringObjectToFront(object, true);
    canvas.discardActiveObject();
    canvas.requestRenderAll();
    setSelectedObject(null);
    saveHistory(canvas);
    console.log("LayerToolbar: Moved to front", {
      name: object.name || object.type,
    });
  };

  // Move layer to back
  const handleSendToBack = () => {
    const object = canvas.getActiveObject();
    if (!canvas || !object) return;
    canvas.sendObjectToBack(object, true);
    canvas.discardActiveObject();
    canvas.requestRenderAll();
    setSelectedObject(null);
    saveHistory(canvas);
    console.log("LayerToolbar: Moved to back", {
      name: object.name || object.type,
    });
  };

  return (
    <Box sx={{ maxHeight: "300px", overflowY: "auto", padding: "8px" }}>
      <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
        <Button
          variant="text"
          startIcon={<KeyboardDoubleArrowUpIcon />}
          size="small"
          disabled={!canvas?.getActiveObject()}
          onClick={handleSendToFront}
          sx={{ color: "black", textTransform: "none" }}
        >
          <Typography variant="body2">To Front</Typography>
        </Button>
        <Button
          variant="text"
          startIcon={<KeyboardDoubleArrowDownIcon />}
          size="small"
          disabled={!canvas?.getActiveObject()}
          onClick={handleSendToBack}
          sx={{ color: "black", textTransform: "none" }}
        >
          <Typography variant="body2">To Back</Typography>
        </Button>
      </Stack>
      <List dense>
        {layers.map((layer, index) => (
          <ListItem
            key={index} // Use index as key since id is removed
            selected={layer.isSelected}
            secondaryAction={
              <Box>
                <IconButton
                  size="small"
                  onClick={() => handleBringForward(layer)}
                  disabled={index === 0}
                >
                  <ArrowUpwardIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => handleSendBackward(layer)}
                  disabled={index === layers.length - 1}
                >
                  <ArrowDownwardIcon fontSize="small" />
                </IconButton>
              </Box>
            }
            sx={{
              backgroundColor: layer.isSelected ? "#e0f7fa" : "transparent",
            }}
          >
            <ListItemText
              primary={layer.name}
              onClick={() => handleSelectLayer(layer)}
              sx={{ cursor: "pointer" }}
            />
          </ListItem>
        ))}
      </List>
    </Box>
  );
};

export default LayerToolbar;